import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase';
import { loadCart, saveCart } from '../services/firestoreService';

const CartContext = createContext();


// Turn any Firebase Storage URL/path into a -w1200.webp (keeps the ?token)
// Use 1200 because we backfilled that size for old images; it's still much lighter.
const toWebpVariant = (url, w = 1200) => {
  if (!url || typeof url !== "string") return url;
  // Cloudinary is handled with its own transform below
  if (url.includes("res.cloudinary.com")) return url;
  if (/\.webp(\?|$)/i.test(url)) return url; // already webp
  const [base, qs = ""] = url.split("?");
  const out = base.replace(/(\.[^.?#]+)$/, `-w${w}.webp`);
  return qs ? `${out}?${qs}` : out;
};

// --- Helper + chooser: always return a lightweight WebP (or Cloudinary tiny) ---
const getCartImageUrl = (productLike, option) => {
  // Convert Firebase Storage URLs/paths to a -w1200.webp (keeps ?token=...)
  const toWebpVariant = (url, w = 1200) => {
    if (!url || typeof url !== "string") return url;
    // Cloudinary handled separately
    if (url.includes("res.cloudinary.com")) return url;
    if (/\.webp(\?|$)/i.test(url)) return url; // already webp
    const [base, qs = ""] = url.split("?");
    const out = base.replace(/(\.[^.?#]+)$/, `-w${w}.webp`);
    return qs ? `${out}?${qs}` : out;
  };

  // Prefer option-level tiny/thumbs first (if variants exist)
  const fromOption =
    option?.thumbnailUrl ||
    option?.lightImageUrl ||
    option?.cardImageUrl ||
    option?.smallImageUrl;

  const candidates = [
    fromOption,
    productLike?.cartImageUrl,       // if already stored
    productLike?.lightImageUrl,
    productLike?.thumbnailUrl,
    productLike?.cardImageUrl,
    productLike?.smallImageUrl,
    productLike?.optimizedImageUrl,
    productLike?.coverImageUrl,
    productLike?.image?.small,
    productLike?.images?.find?.(img => img?.isPrimary)?.thumbnailUrl,
    productLike?.images?.[0]?.thumbnailUrl,
    productLike?.images?.[0]?.smallUrl,
    productLike?.principalImageUrl,  // last resort
  ].filter(Boolean);

  let src = candidates[0] || "";

  // Cloudinary → tiny auto-format/quality thumb
  if (src.includes("res.cloudinary.com") && src.includes("/upload/")) {
    return src.replace("/upload/", "/upload/f_auto,q_auto:eco,c_fill,g_auto,w_160,h_160/");
  }

  // Firebase/other → derived WebP we backfilled (-w1200.webp)
  return toWebpVariant(src, 1200) || "/images/placeholder-96.png";
};


export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Normalize items to ensure a light image exists
  const normalizeCart = (items) =>
    (items || []).map((it) => ({
      ...it,
      cartImageUrl: toWebpVariant(it.cartImageUrl, 1200) || getCartImageUrl(it, it.option),
    }));

  useEffect(() => {
    const fetchCart = async () => {
      let incoming = [];
      if (auth.currentUser) {
        const cartData = await loadCart();
        incoming = cartData || [];
      } else {
        const savedCart = localStorage.getItem('cart');
        incoming = savedCart ? JSON.parse(savedCart) : [];
      }

      const normalized = normalizeCart(incoming);
      setCart(normalized);

      // Persist normalized version back
      if (auth.currentUser) {
        try { await saveCart(normalized); } catch (e) { console.error('Failed to save cart:', e); }
      } else {
        localStorage.setItem('cart', JSON.stringify(normalized));
      }
    };

    fetchCart();

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setCart([]);
        localStorage.removeItem('cart');
      }
    });

    return () => unsubscribe();
  }, []);

  const addToCart = async (product, onProductAdded) => {
    if (!product || !product.id || product.price === undefined || product.stock === undefined) {
      console.error('Invalid product data:', product);
      return;
    }

    const existingItem = cart.find((item) => {
      if (item.id === product.id) {
        if (item.option && product.option) return item.option.name === product.option.name;
        return !item.option && !product.option;
      }
      return false;
    });

    const priceToUse = product.option ? product.option.price : product.price;
    const tinyUrl = getCartImageUrl(product, product.option);

    let updatedCart;

    if (existingItem) {
      updatedCart = cart.map((item) =>
        item === existingItem
          ? {
              ...item,
              quantity: Math.min(item.quantity + 1, item.stock),
              // Si viene un mensaje nuevo desde el producto, lo guardamos
              ...(product.tarjetaMessage ? { tarjetaMessage: product.tarjetaMessage } : {}),
            }
          : item
      );
    } else {
      // ...

      // Store only what we actually need in the cart line to keep it light
      const itemToStore = {
        id: product.id,
        name: product.name,
        price: priceToUse,
        stock: product.stock,
        quantity: 1,
        cartImageUrl: tinyUrl, // 🔑 lightweight image persistida
        ...(product.tarjetaMessage ? { tarjetaMessage: product.tarjetaMessage } : {}),

      };

      // Solo agregamos option si realmente existe
      if (product.option && product.option.name) {
        itemToStore.option = { name: product.option.name };
      }

      // Solo agregamos principalImageUrl si viene definido
      if (product.principalImageUrl) {
        itemToStore.principalImageUrl = product.principalImageUrl;
      }

      updatedCart = [...cart, itemToStore];
    }


    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));

    if (auth.currentUser) {
      try { await saveCart(updatedCart); } catch (err) { console.error('Failed to save cart:', err); }
    }

    if (onProductAdded) onProductAdded();
  };

  const removeFromCart = async (productId, optionName) => {
    const updatedCart = cart.filter((item) => {
      if (item.id === productId) {
        if (item.option && optionName) return item.option.name !== optionName;
        return false;
      }
      return true;
    });

    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));

    if (auth.currentUser) {
      try { await saveCart(updatedCart); } catch (err) { console.error('Failed to save cart:', err); }
    }
  };

  const updateQuantity = async (productId, quantity, optionName) => {
    const updatedCart = cart.map((item) =>
      item.id === productId && (!item.option || item.option.name === optionName)
        ? { ...item, quantity: Math.min(Math.max(quantity, 1), item.stock) }
        : item
    );

    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));

    if (auth.currentUser) {
      try { await saveCart(updatedCart); } catch (err) { console.error('Failed to save cart:', err); }
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, setCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
